import React from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import Seo from '../components/Seo';

export const DEFAULT_TITLE = 'Sovrynn — Your operation. Your intelligence.';
export const DEFAULT_DESCRIPTION =
  'Your operation. Your intelligence. Sovrynn is the Compliance Intelligence platform for owner-operators, local and regional carriers, new authority holders, and small fleets.';

export const SITE_ORIGIN = 'https://sovrynn.co';
export const HOME_CANONICAL = `${SITE_ORIGIN}/`;
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

const HOME_TITLE = 'Sovrynn — DOT compliance, handled';
const HOME_DESCRIPTION =
  'Sovrynn watches your deadlines, keeps your driver and truck files in order, and tells you what to do next. See your free DRI Score™ in under 5 minutes.';
const HOME_OG_IMAGE_ALT = 'Sovrynn: DOT, handled.';

const NOINDEX_PATHS = ['/landing-legacy', '/landing-v2'];

// Per-route page titles and meta descriptions.
// Blog post pages (/blog/:slug) are intentionally absent — BlogPostPage sets
// its own meta from the post's frontmatter (title + excerpt).
const ROUTE_META = [
  {
    path: '/',
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    canonical: HOME_CANONICAL,
    ogImageAlt: HOME_OG_IMAGE_ALT,
  },
  {
    path: '/landing',
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    canonical: HOME_CANONICAL,
    ogImageAlt: HOME_OG_IMAGE_ALT,
  },
  {
    path: '/landing-legacy',
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  {
    path: '/landing-v2',
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  {
    path: '/login',
    title: 'Sign In — Sovrynn',
    description: 'Sign in to your Sovrynn account to view your DRI Score™, Compliance Passport™, and real-time compliance status.',
  },
  {
    path: '/register',
    title: 'Get Started Free — Sovrynn',
    description: 'Create a free Sovrynn account and get your DRI Score™ in under 5 minutes. No credit card required.',
  },
  {
    path: '/onboarding',
    title: 'Onboarding — Sovrynn',
    description: 'Set up your Sovrynn account and get your compliance intelligence dashboard ready.',
  },
  {
    path: '/forgot-password',
    title: 'Forgot Password — Sovrynn',
    description: 'Reset your Sovrynn account password.',
  },
  {
    path: '/reset-password',
    title: 'Reset Password — Sovrynn',
    description: 'Choose a new password for your Sovrynn account.',
  },
  {
    path: '/terms',
    title: 'Terms of Service — Sovrynn',
    description: 'The terms of service governing your use of Sovrynn, the compliance intelligence platform for independent commercial operators.',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy — Sovrynn',
    description: 'How Sovrynn collects, uses, and protects your data.',
  },
  {
    path: '/sms-consent',
    title: 'SMS Consent — Sovrynn',
    description: 'Sovrynn SMS messaging terms and consent information.',
  },
  {
    path: '/blog',
    title: 'Blog — Sovrynn',
    description: 'Compliance intelligence insights for owner-operators and small trucking fleets — FMCSA compliance, DRI Score™, authority management, and staying road-ready.',
  },
  {
    path: '/p/:shareToken',
    title: 'Compliance Passport™ — Sovrynn',
    description: 'A verified, shareable compliance credential powered by live FMCSA data.',
  },
  {
    path: '/passport/:passportId',
    title: 'Compliance Passport™ — Sovrynn',
    description: 'A verified, shareable compliance credential powered by live FMCSA data.',
  },
  // Authenticated app pages
  { path: '/dashboard', title: 'Dashboard — Sovrynn', description: 'Your compliance intelligence, at a glance.' },
  { path: '/documents', title: 'Documents — Sovrynn', description: 'Manage your compliance documents and expirations.' },
  { path: '/vehicles', title: 'Vehicles — Sovrynn', description: 'Track vehicle readiness and maintenance compliance.' },
  { path: '/vehicles/:id', title: 'Vehicle Details — Sovrynn', description: 'Vehicle readiness and compliance details.' },
  { path: '/drivers', title: 'Drivers — Sovrynn', description: 'Manage driver compliance across your fleet.' },
  { path: '/drivers/:id', title: 'Driver Profile — Sovrynn', description: 'Driver compliance profile and readiness.' },
  { path: '/fleet/drivers/:driverId', title: 'Driver Profile — Sovrynn', description: 'Driver compliance profile and readiness.' },
  { path: '/authority', title: 'Authority — Sovrynn', description: 'Monitor your operating authority and FMCSA standing.' },
  { path: '/authority-copilot', title: 'Authority Copilot — Sovrynn', description: 'Guided help getting and keeping your authority compliant.' },
  { path: '/authority-copilot/plan', title: 'Authority Copilot Plan — Sovrynn', description: 'Your step-by-step authority compliance plan.' },
  { path: '/passport', title: 'Compliance Passport™ — Sovrynn', description: 'Your verified, shareable compliance credential.' },
  { path: '/pricing', title: 'Pricing — Sovrynn', description: 'Simple pricing for owner-operators and small fleets. Free to start.' },
  { path: '/clearinghouse', title: 'Clearinghouse — Sovrynn', description: 'FMCSA Drug & Alcohol Clearinghouse status and queries.' },
  { path: '/clearinghouse/queries', title: 'Clearinghouse Queries — Sovrynn', description: 'Track your Clearinghouse query history.' },
  { path: '/ifta', title: 'IFTA — Sovrynn', description: 'Track IFTA fuel tax reporting deadlines and records.' },
  { path: '/audit', title: 'Audit Checklist — Sovrynn', description: 'Stay audit-ready with a guided compliance checklist.' },
  { path: '/timeline', title: 'Compliance Timeline — Sovrynn', description: 'Upcoming compliance deadlines and renewals.' },
  { path: '/alerts', title: 'Alerts — Sovrynn', description: 'Compliance alerts and risk notifications.' },
  { path: '/violations', title: 'Violations — Sovrynn', description: 'Inspection and violation history.' },
  { path: '/broker-view', title: 'Counterparty View — Sovrynn', description: 'See your compliance profile the way brokers see it.' },
  { path: '/counterparty', title: 'Counterparty View — Sovrynn', description: 'See your compliance profile the way brokers see it.' },
  { path: '/checklists', title: 'Checklists — Sovrynn', description: 'Compliance checklists for your operation.' },
  { path: '/analytics', title: 'Analytics — Sovrynn', description: 'Compliance analytics and DRI Score™ trends.' },
  { path: '/settings', title: 'Settings — Sovrynn', description: 'Manage your account, billing, and preferences.' },
  { path: '/support', title: 'Support — Sovrynn', description: 'Get help from the Sovrynn team.' },
  { path: '/fc', title: 'Admin — Sovrynn', description: 'Sovrynn administration.' },
  { path: '/fc/insurance-leads', title: 'Insurance Leads — Sovrynn', description: 'Sovrynn administration.' },
];

export default function RouteSeo() {
  const { pathname } = useLocation();

  // Blog posts manage their own meta from frontmatter
  if (matchPath('/blog/:slug', pathname)) {
    return null;
  }

  const match = ROUTE_META.find((entry) => matchPath(entry.path, pathname));
  const noindex = NOINDEX_PATHS.includes(pathname) || Boolean(match?.noindex);
  return (
    <Seo
      title={match ? match.title : 'Sovrynn'}
      description={match ? match.description : DEFAULT_DESCRIPTION}
      canonical={match?.canonical}
      noindex={noindex}
      ogImage={DEFAULT_OG_IMAGE}
      ogImageAlt={match?.ogImageAlt || 'Sovrynn'}
    />
  );
}
